import {type ICartCalculation, type ICartCalculationRecord, type ICartMetaData, type IShippingMethod} from '../../../@type/woocommerce/cart-calculation';
import {type DispatchAction, store} from '../store';
import {DispatchActionType} from '../models/DispatchActionType';
import {type WCCartItem} from '../../../@type/woocommerce/cart-item';
import {type KeyValue} from '../../../@type/dictionary';
import {getLocaleText} from '../util/translation';
import {MerchantConfiguration} from './merchantConfigurationReducer';
import {createDispatchUpdate} from './initialState';

/**
 * Reducer for handling cart calculation state changes.
 * @param state
 * @param action
 */
export function cartReducer(state: ICartCalculationRecord, action: DispatchAction<unknown>): ICartCalculationRecord {
	switch (action.type) {
		case DispatchActionType.DEFAULT_CART_CONTENTS:
			return {
				...state,
				0: {
					...state['0'],
					cart: (action as DispatchAction<WCCartItem[]>).payload,
				},
			};
		case DispatchActionType.DEFAULT_CART_CALCULATION:
			return {
				...state,
				0: {
					...(action as DispatchAction<ICartCalculation>).payload,
				},
			};
		case DispatchActionType.CART_CALCULATION:
			return {
				...(action as DispatchAction<ICartCalculationRecord>).payload,
			};
		case DispatchActionType.CART_SHIPPING_SELECTION: {
			const {payload} = action as DispatchAction<ICartShippingSelection>;
			const newState = {...state};

			if (!newState?.[payload.cartKey]?.package_record) {
				return newState;
			}

			const packageRecord = (newState[payload.cartKey]!).package_record;
			if (!packageRecord[payload.shippingPackageKey]) {
				return newState;
			}

			(packageRecord[payload.shippingPackageKey]!).selected_method = payload.packageMethodId;
			return newState;
		}

		default:
			return {
				...state,
			};
	}
}

// ========================== Action Creators =======================

export const updateCartCalculation = createDispatchUpdate<ICartCalculationRecord>(DispatchActionType.CART_CALCULATION);
export const updateDefaultCartContents = createDispatchUpdate<WCCartItem[]>(DispatchActionType.DEFAULT_CART_CONTENTS);
export const updateCartPackageShippingMethod = createDispatchUpdate<ICartShippingSelection>(DispatchActionType.CART_SHIPPING_SELECTION);

interface ICartShippingSelection {
	cartKey: string;
	shippingPackageKey: string;
	packageMethodId: string;
}

// ========================== Store Selectors =======================

export function createCartSelectors(cartKey = '0') {
	return {
		selectedShippingMethod: (packageKey = '0') => store.getState().calculatedCarts[cartKey]?.package_record?.[packageKey]?.selected_method ?? '',
		selectedShippingMethodDetails: (packageKey = '0') => store.getState().calculatedCarts[cartKey]?.package_record?.[packageKey] ?? null,
		contents: () => store.getState().calculatedCarts[cartKey]?.cart ?? [],
		subtotal: () => store.getState().calculatedCarts[cartKey]?.summary.subtotal ?? 0,
		feeTotal: (fee: string) => store.getState().calculatedCarts[cartKey]?.summary.fees_record[fee] ?? 0,
		totalAppliedFees: () => Object.entries(store.getState().calculatedCarts[cartKey]?.summary.fees_record ?? {}).reduce((previousValue, [_, value]) => previousValue + (value ?? 0), 0),
		couponTotal: (coupon: string) => store.getState().calculatedCarts[cartKey]?.summary.coupons_record[coupon] ?? 0,
		totalAppliedCoupons: () => Object.entries(store.getState().calculatedCarts[cartKey]?.summary.coupons_record ?? {}).reduce((previousValue, [_, value]) => previousValue + (value ?? 0), 0),
		couponRecord: () => store.getState().calculatedCarts[cartKey]?.summary.coupons_record ?? {},
		giftCardTotal: (giftCard: string) => store.getState().calculatedCarts[cartKey]?.summary.gift_card_record?.[giftCard] ?? 0,
		totalAppliedGiftCards: () => Object.entries(store.getState().calculatedCarts[cartKey]?.summary.gift_card_record ?? {}).reduce((previousValue, [_, value]) => previousValue + (value ?? 0), 0),
		totalShipping: () => store.getState().calculatedCarts[cartKey]?.summary.total_shipping ?? 0,
		totalTax: () => store.getState().calculatedCarts[cartKey]?.summary.total_tax ?? 0,
		total: () => store.getState().calculatedCarts[cartKey]?.summary.total ?? 0,
		shippingMethods(packageKey = '0') {
			const methods = store.getState().calculatedCarts[cartKey]?.package_record?.[packageKey]?.methods ?? {} as Record<string, IShippingMethod | undefined>;

			return Object.entries(methods).map(([id, method]) => {
				(method!).id = id;
				return method!;
			});
		},
	};
}

export const DefaultCart = createCartSelectors('0');

export const Carts = {
	anyShippingMethodsAvailable() {
		for (const cartKey of Object.keys(store.getState().calculatedCarts)) {
			const calculatedCart = store.getState().calculatedCarts[cartKey];
			if (!calculatedCart) {
				continue;
			}

			for (const packageKey of Object.keys(calculatedCart.package_record)) {
				const shippingPackage = calculatedCart.package_record[packageKey];
				if (!shippingPackage || Object.entries(shippingPackage.methods).length === 0) {
					continue;
				}

				return true;
			}
		}

		return false;
	},
	virtual() {
		for (const cartKey of Object.keys(store.getState().calculatedCarts)) {
			const calculatedCart = store.getState().calculatedCarts[cartKey];
			if (!calculatedCart) {
				continue;
			}

			if (!calculatedCart.cart_meta.is_virtual) {
				return false;
			}
		}

		return true;
	},
	anyVirtual() {
		const carts = store.getState().calculatedCarts;
		for (const [_, cart] of Object.entries(carts)) {
			if (!cart) {
				continue;
			}

			for (const [__, item] of Object.entries(cart)) {
				if (item.virtual) {
					return true;
				}
			}
		}

		return false;
	},
	collectSelectedShipping() {
		const carts = store.getState().calculatedCarts;
		const selectedShippingMethods: Array<{
			methodKey: string;
			selectedShipping: string;
		}> = [];

		for (const cart of Object.values(carts)) {
			if (!cart) {
				continue;
			}

			for (const [packageKey, packageRecord] of Object.entries(cart.package_record ?? {})) {
				if (!packageRecord) {
					continue;
				}

				selectedShippingMethods.push({
					methodKey: `${packageKey}`,

					// PackageKey: `${packageKey}`,
					selectedShipping: packageRecord.selected_method,
				});
			}
		}

		return selectedShippingMethods;
	},
	subscriptionPresent() {
		for (const cartKey of Object.keys(store.getState().calculatedCarts)) {
			const calculatedCart = store.getState().calculatedCarts[cartKey];
			if (!calculatedCart) {
				continue;
			}

			if (calculatedCart.cart_meta.subscription) {
				return true;
			}
		}

		return false;
	},
	total() {
		let total = 0;

		for (const cartKey of Object.keys(store.getState().calculatedCarts)) {
			const calculatedCart = store.getState().calculatedCarts[cartKey];
			if (!calculatedCart) {
				continue;
			}

			total += calculatedCart.summary.total;
		}

		return total;
	},
};

/**
 * Selects and reorganizes cart data for the default cart into something that is more useful for rendering.
 */
export const selectDefaultCartViewData = cartSummaryViewData('0');

type CartSummaryViewData = {
	cartSummary: Array<KeyValue<string, number>>;
	cartMeta: ICartMetaData;
};

export function cartSummaryViewData(cartKey: string): () => CartSummaryViewData {
	return () => {
		const calculatedCart = store.getState().calculatedCarts[cartKey];
		if (!calculatedCart) {
			return {
				cartSummary: new Array<KeyValue<string, number>>(),
				cartMeta: {
					is_virtual: false,
				},
			};
		}

		const cartSummary: Array<KeyValue<string, number>> = [];
		const cartMeta = calculatedCart.cart_meta;

		// Subtotal
		cartSummary.push({
			key: getLocaleText('Subtotal'),
			value: calculatedCart.summary.subtotal,
		});

		if (calculatedCart.cart.length > 0) {
			// Coupons
			for (const [coupon, amount] of Object.entries(calculatedCart.summary.coupons_record)) {
				if (!amount) {
					continue;
				}

				cartSummary.push({
					key: `${getLocaleText('Coupon')} - (${coupon}) <button class="pp-coupon-remove-button" data-coupon="${coupon}" type="button" tabindex="0">[&times;]</button>`,
					value: -amount,
				});
			}
		}

		// Fees
		for (const [fee, amount] of Object.entries(calculatedCart.summary.fees_record)) {
			if (!amount) {
				continue;
			}

			cartSummary.push({
				key: `Fee - (${fee})`,
				value: amount,
			});
		}

		// Shipping
		if (!calculatedCart.cart_meta.is_virtual) {
			cartSummary.push({
				key: getLocaleText('Shipping'),
				value: calculatedCart.summary.total_shipping,
			});
		}

		// Tax
		if (MerchantConfiguration.tax.displayMode() === 'excludeTax' && calculatedCart.summary.total_tax !== 0) {
			if (calculatedCart.summary.tax_lines?.length) {
				calculatedCart.summary.tax_lines.forEach(tax_line => {
					cartSummary.push({
						key: tax_line['label'] === 'Tax' ? getLocaleText('Tax') : tax_line['label'],
						value: parseFloat(tax_line['amount']),
					});
				});
			} else {
				cartSummary.push({
					key: getLocaleText('Tax'),
					value: calculatedCart.summary.total_tax,
				});
			}
		}

		if (calculatedCart.cart.length > 0) {
			// Gift cards
			for (const [giftCard, amount] of Object.entries(calculatedCart.summary.gift_card_record)) {
				if (!amount) {
					continue;
				}

				cartSummary.push({
					key: `Gift card - (${giftCard})`,
					value: -amount,
				});
			}
		}

		// Total
		cartSummary.push({
			key: getLocaleText('Total'),
			value: calculatedCart.summary.total,
		});

		return {cartSummary, cartMeta};
	};
}
